import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type AccountOpeningDTO, type AccountOpeningOperationRuns, type AccountOpeningTypeSelector, type AccountType } from '../../../models/AccountOpening'
import {
  type AccountOpeningCollectionStep,
  type AccountOpeningDescriptor,
  type AccountOpeningItemCopy,
  type AccountOpeningOperationStep,
  type AccountOpeningStep,
  type AccountOpeningStepId,
} from '../../../models/AccountOpeningDescriptor'
import { operationSucceeded, outcomeMetadata, type SubmissionOutcome } from '../domain/connectorResult'
import { irreversibleStep, isStepConfirmed, isStepReached, resolveFlow, stepStatuses, type FlowState, type StepStatus } from '../domain/flow'
import { confirmScopes, flowCollections, itemsOf, preparationSignature, type CollectionContext, type ResolvedItem } from '../domain/collections'
import { itemCopy } from '../descriptor/stepCopy'
import { useAccountOpeningPersistence, type LoadOpeningOptions } from './useAccountOpeningPersistence'
import { useCollectionSchemas, type CollectionSchemas } from './useCollectionSchemas'
import { shouldAutosave, useDraftAutosave } from './useDraftAutosave'
import { type ContactOption, type ItemPatch, type WizardAccount, type WizardContext, type WizardParty } from '../domain/types'
import { draftReducer, EMPTY_DRAFT, type DraftAction, type WizardDraft } from '../domain/wizardDraft'

interface WizardOptions {
  ctx: WizardContext
  accountTypes: AccountType[]
  selectors: AccountOpeningTypeSelector[]
  descriptor: AccountOpeningDescriptor | undefined
  contactOptions: ContactOption[]
}

export interface WizardApi {
  accounts: WizardAccount[]
  parties: WizardParty[]
  openingId: string | null
  steps: AccountOpeningStep[]
  statuses: Record<string, StepStatus>
  reachedStep: (id: AccountOpeningStepId) => boolean
  stepConfirmed: (step: AccountOpeningStep) => boolean
  readOnly: boolean
  collectionCtx: CollectionContext
  itemsCopy: Required<AccountOpeningItemCopy>
  confirmations: Record<AccountOpeningStepId, string[]>
  operationResults: Record<AccountOpeningStepId, SubmissionOutcome>
  operationRuns: AccountOpeningOperationRuns
  submittedResult: SubmissionOutcome | null
  savedMetadata: Record<string, unknown>
  preparationSignatures: Record<string, string>
  saveError: string | null
  isSaving: boolean
  canDiscard: (stepId: AccountOpeningStepId) => boolean
  editItem: (item: ResolvedItem, patch: ItemPatch, stepId: AccountOpeningStepId) => void
  applyIntake: (accounts: WizardAccount[]) => void
  confirmStep: (step: AccountOpeningCollectionStep) => Promise<void>
  discardStepChanges: (stepId: AccountOpeningStepId) => void
  confirmItem: (step: AccountOpeningCollectionStep, ref: string) => void
  hydrateCollection: CollectionSchemas['hydrateCollection']
  prefillCollection: CollectionSchemas['prefillCollection']
  fillValues: CollectionSchemas['fillValues']
  recordOperationResult: (step: AccountOpeningOperationStep, outcome: SubmissionOutcome) => Promise<void>
  startNew: (accounts: WizardAccount[]) => void
  loadOpening: (dto: AccountOpeningDTO, options: LoadOpeningOptions) => void
  reset: () => void
}

const DRAFT_REPLACING_ACTIONS: ReadonlySet<DraftAction['type']> = new Set(['reset', 'started', 'loaded'])

export const useAccountOpeningWizard = ({ ctx, accountTypes, selectors, descriptor, contactOptions }: WizardOptions): WizardApi => {
  const [draft, setDraft] = useState<WizardDraft>(EMPTY_DRAFT)

  const draftRef = useRef(draft)
  const generation = useRef(0)
  const draftGenerations = useRef(new WeakMap<WizardDraft, number>())
  const onApplied = useRef<(action: DraftAction, next: WizardDraft) => void>(() => undefined)
  const apply = useCallback((action: DraftAction): WizardDraft => {
    const next = draftReducer(draftRef.current, action)
    if (DRAFT_REPLACING_ACTIONS.has(action.type)) {
      generation.current += 1
    }
    draftGenerations.current.set(next, generation.current)
    draftRef.current = next
    setDraft(next)
    onApplied.current(action, next)
    return next
  }, [])

  const { accounts, parties, confirmations, operationResults, operationRuns, openingId, readOnly, confirmedSnapshot } = draft

  const steps = useMemo(() => (descriptor ? resolveFlow(descriptor, 'edit') : []), [descriptor])

  const submitStepId = useMemo(() => (descriptor ? irreversibleStep(descriptor)?.id : undefined), [descriptor])
  const submitOutcome = submitStepId ? (operationResults[submitStepId] ?? null) : null
  const submittedResult = operationSucceeded(submitOutcome) ? submitOutcome : null

  const copy = useMemo(() => itemCopy(descriptor), [descriptor])
  const collectionCtx: CollectionContext = useMemo(
    () => ({ accountTypes, selectors, accounts, parties, readOnly, copy }),
    [accountTypes, selectors, accounts, parties, readOnly, copy],
  )
  const flowState: FlowState = useMemo(() => ({ ...collectionCtx, confirmations, operationResults }), [collectionCtx, confirmations, operationResults])
  const statuses = useMemo(() => stepStatuses(steps, flowState), [steps, flowState])
  const reachedStep = useCallback((id: AccountOpeningStepId) => isStepReached(statuses, id), [statuses])
  const stepConfirmed = useCallback((step: AccountOpeningStep) => isStepConfirmed(step, flowState), [flowState])

  const persistence = useAccountOpeningPersistence({ ctx, descriptor, steps, accountTypes, selectors, contactOptions, submitStepId })
  const { save, loadOpening: decodeOpening, clearSaveError } = persistence

  const persist = useCallback(
    async (snapshot: WizardDraft, overrides?: Parameters<typeof save>[1]) => {
      const savedGeneration = draftGenerations.current.get(snapshot) ?? generation.current
      const saved = await save(snapshot, overrides)
      if (saved && generation.current === savedGeneration) {
        apply({ type: 'savedAs', ...saved })
      }
    },
    [save, apply],
  )

  const getDraft = useCallback(() => draftRef.current, [])
  const { schedule: scheduleAutosave, run: runSave, flush: flushAutosave } = useDraftAutosave({ getDraft, persist, saveDetached: save })

  useEffect(() => {
    onApplied.current = (action, next) => {
      const submitted = !!submitStepId && operationSucceeded(next.operationResults[submitStepId] ?? null)
      if (shouldAutosave(action, next, submitted)) {
        scheduleAutosave()
      }
    }
  }, [submitStepId, scheduleAutosave])

  const { hydrateCollection, prefillCollection, fillValues } = useCollectionSchemas({ ctx, collectionCtx, steps, readOnly, contactOptions, apply })

  const reset = useCallback(() => {
    flushAutosave()
    apply({ type: 'reset' })
    clearSaveError()
  }, [apply, clearSaveError, flushAutosave])

  const editItem = useCallback(
    (item: ResolvedItem, patch: ItemPatch, stepId: AccountOpeningStepId) => {
      apply({ type: 'itemEdited', item, patch, scopes: confirmScopes(steps, item, patch, stepId) })
    },
    [apply, steps],
  )

  const applyIntake = useCallback(
    (intakeAccounts: WizardAccount[]) => {
      apply({ type: 'intakeApplied', accounts: intakeAccounts })
      clearSaveError()
    },
    [apply, clearSaveError],
  )

  const preparationSignatures = useMemo(
    () => Object.fromEntries(flowCollections(steps).map((collection) => [collection, preparationSignature(steps, collection, collectionCtx)])),
    [steps, collectionCtx],
  )

  const confirmStep = useCallback(
    async (step: AccountOpeningCollectionStep) => {
      const items = itemsOf(step.collection, collectionCtx)
      const next = apply({ type: 'stepConfirmed', stepId: step.id, refs: items.map((item) => item.ref), snapshot: !!step.confirm?.discardLabel })
      await runSave(() => persist(next))
    },
    [collectionCtx, apply, persist, runSave],
  )

  const discardStepChanges = useCallback(
    (stepId: AccountOpeningStepId) => {
      apply({ type: 'stepChangesDiscarded', stepId })
    },
    [apply],
  )

  const canDiscard = useCallback((stepId: AccountOpeningStepId) => confirmedSnapshot?.stepId === stepId, [confirmedSnapshot])

  const confirmItem = useCallback(
    (step: AccountOpeningCollectionStep, ref: string) => {
      apply({ type: 'itemConfirmed', stepId: step.id, ref })
    },
    [apply],
  )

  const startNew = useCallback(
    (initialAccounts: WizardAccount[]) => {
      apply({ type: 'started', accounts: initialAccounts })
      clearSaveError()
    },
    [apply, clearSaveError],
  )

  const loadOpening = useCallback(
    (dto: AccountOpeningDTO, options: LoadOpeningOptions) => {
      apply({ type: 'loaded', loaded: decodeOpening(dto), readOnly: options.readOnly })
    },
    [decodeOpening, apply],
  )

  const recordOperationResult = useCallback(
    async (step: AccountOpeningOperationStep, outcome: SubmissionOutcome) => {
      const next = apply({ type: 'operationRecorded', stepId: step.id, outcome, at: new Date().toISOString() })
      if (!next.openingId) {
        return
      }
      await runSave(() =>
        persist(next, step.irreversible && operationSucceeded(outcome) ? { status: 'submitted', metadata: outcomeMetadata(outcome) } : undefined),
      )
    },
    [apply, persist, runSave],
  )

  return {
    accounts,
    parties,
    openingId,
    steps,
    statuses,
    reachedStep,
    stepConfirmed,
    readOnly,
    collectionCtx,
    itemsCopy: copy,
    confirmations,
    operationResults,
    operationRuns,
    submittedResult,
    savedMetadata: draft.savedMetadata,
    preparationSignatures,
    saveError: persistence.saveError,
    isSaving: persistence.isSaving,
    canDiscard,
    editItem,
    applyIntake,
    confirmStep,
    discardStepChanges,
    confirmItem,
    hydrateCollection,
    prefillCollection,
    fillValues,
    recordOperationResult,
    startNew,
    loadOpening,
    reset,
  }
}
